import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { AlertCircle, KeyRound, ShieldOff } from 'lucide-react';
import { validatePhoneNumber } from '@/lib/profilesSync';
import { generateSalt, hashPin, verifyPin } from '@/lib/pinCrypto';
import { revokeTrustedDevices, removeLocalDeviceToken } from '@/lib/trustedDeviceSync';
import { db, storage } from '@/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useUser } from '@/context/UserContext';


/**
 * Valida el formato de un PIN: solo dígitos, 4 a 6 caracteres.
 */
function validatePinFormat(pin) {
  if (!/^\d{4,6}$/.test(pin || '')) {
    return { valid: false, message: 'El PIN debe tener entre 4 y 6 dígitos numéricos.' };
  }
  return { valid: true, message: '' };
}

/**
 * Modal de configuración individual del perfil actualmente seleccionado.
 * Edita solo el perfil activo sin afectar a otros.
 */
export function ProfileSettingsModal({
  open,
  onOpenChange,
  selectedUser,
  profiles,
  updateProfilesConfig,
  updateOwnWorkerProfile,
  userRole,
  workspaceId: workspaceIdProp,
}) {
  const isWorker = userRole === 'worker';

  const { toast } = useToast();
  const { unlockProfile } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  const [phoneValidationErrors, setPhoneValidationErrors] = useState({});

  // Estado local para el perfil en edición
  const [editingProfile, setEditingProfile] = useState(null);

  // Estados para carga diferida de imagen de acceso rápido (no se sube a Firebase hasta Guardar)
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState(null);
  const [pendingImageDelete, setPendingImageDelete] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Estado local para el formulario de PIN (crear o cambiar)
  const [currentPinInput, setCurrentPinInput] = useState('');
  const [newPinInput, setNewPinInput] = useState('');
  const [confirmPinInput, setConfirmPinInput] = useState('');
  const [isSavingPin, setIsSavingPin] = useState(false);
  const [showRemovePinConfirm, setShowRemovePinConfirm] = useState(false);
  const [isRemovingPin, setIsRemovingPin] = useState(false);

  // Cargar el perfil activo al abrir el modal
  useEffect(() => {
    if (open && selectedUser && profiles && profiles.length > 0) {
      const activeProfile = profiles.find(p => (p.id ?? p.name) === selectedUser);
      if (activeProfile) {
        setEditingProfile({ ...activeProfile });
      }
    }
    if (!open) {
      // Limpiar campos de PIN al cerrar el modal (nunca dejar PIN en memoria de un formulario abierto)
      setCurrentPinInput('');
      setNewPinInput('');
      setConfirmPinInput('');
      // Limpiar estado pendiente de imagen al cerrar sin guardar (revocar preview local)
      setPendingImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPendingImageFile(null);
      setPendingImageDelete(false);
    }
  }, [open, selectedUser, profiles]);

  // Revocar la URL de preview local al sustituirla o al desmontar el componente
  useEffect(() => {
    return () => {
      if (pendingImagePreview) {
        URL.revokeObjectURL(pendingImagePreview);
      }
    };
  }, [pendingImagePreview]);

  const handleCancel = () => {
    // Descartar imagen pendiente sin tocar Firebase Storage
    if (pendingImagePreview) {
      URL.revokeObjectURL(pendingImagePreview);
    }
    setPendingImageFile(null);
    setPendingImagePreview(null);
    setPendingImageDelete(false);
    setEditingProfile(null);
    onOpenChange(false);
  };

  /**
   * Crea o cambia el PIN del perfil actual.
   * - Si el perfil ya tiene pinHash, exige el PIN actual correcto antes de aceptar el nuevo.
   * - Si el perfil no tiene PIN, no se exige PIN actual (creación).
   * - Guarda solo pinHash/pinSalt/pinUpdatedAt (nunca el PIN en texto plano).
   */
  /**
   * Revoca todos los dispositivos confiables del perfil actual.
   * - Incrementa trustedDevicesVersion en Firestore
   * - Elimina el token local del workspace/perfil
   * - No modifica el PIN
   */
  const handleRevokeDevices = async () => {
    if (!editingProfile?.pinHash) {
      toast({
        title: 'No hay PIN configurado',
        description: 'Solo perfiles con PIN tienen dispositivos confiables.',
        variant: 'destructive',
      });
      return;
    }

    const profileId = editingProfile.id ?? editingProfile.name;

    try {
      if (workspaceIdProp) {
        await revokeTrustedDevices(workspaceIdProp, db, profileId);
        removeLocalDeviceToken(workspaceIdProp, profileId);
      }
      toast({
        title: '✅ Dispositivos revocados',
        description: 'Todos los dispositivos confiables han sido invalidados.',
        duration: 2500,
      });
    } catch (error) {
      console.error('[ProfileSettingsModal] Error revoking devices:', error);
      toast({
        title: 'Error al revocar dispositivos',
        description: 'No se pudieron revocar los dispositivos. Intenta de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const handleSavePin = async () => {
    if (!editingProfile || !profiles || !selectedUser) return;

    const hasExistingPin = !!editingProfile.pinHash;

    if (hasExistingPin) {
      const isCurrentCorrect = await verifyPin(
        currentPinInput,
        editingProfile.pinHash,
        editingProfile.pinSalt
      );
      if (!isCurrentCorrect) {
        toast({
          title: 'PIN actual incorrecto',
          description: 'El PIN actual introducido no coincide.',
          variant: 'destructive',
        });
        return;
      }
    }

    const formatCheck = validatePinFormat(newPinInput);
    if (!formatCheck.valid) {
      toast({
        title: 'PIN inválido',
        description: formatCheck.message,
        variant: 'destructive',
      });
      return;
    }

    if (newPinInput !== confirmPinInput) {
      toast({
        title: 'Los PIN no coinciden',
        description: 'El PIN nuevo y su repetición deben ser iguales.',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingPin(true);
    try {
      const salt = await generateSalt();
      const { hash, iterations, version } = await hashPin(newPinInput, salt);

      const profileId = editingProfile.id ?? editingProfile.name;
      const pinUpdatedAt = new Date().toISOString();

      // Revocar dispositivos confiables al crear/cambiar PIN
      if (workspaceIdProp) {
        try {
          await revokeTrustedDevices(workspaceIdProp, db, profileId);
          removeLocalDeviceToken(workspaceIdProp, profileId);
        } catch (revokeError) {
          console.warn('[ProfileSettingsModal] Warning revoking devices during PIN save:', revokeError);
          // No bloquear guardado del PIN si revocación falla
        }
      }

      // Trabajador: guarda el PIN en su propio workerProfiles/{profileId}
      // vía updateOwnWorkerProfile. Nunca escribe en settings/profiles.
      if (isWorker) {
        if (typeof updateOwnWorkerProfile !== 'function') {
          toast({
            title: 'Error al guardar el PIN',
            description: 'No se pudo guardar el PIN.',
            variant: 'destructive',
          });
          return;
        }

        const success = await updateOwnWorkerProfile(profileId, {
          pinHash: hash,
          pinSalt: salt,
          pinIterations: iterations,
          pinVersion: version,
          pinUpdatedAt,
        });

        if (!success) {
          toast({
            title: 'Error al guardar el PIN',
            description: 'No se pudo guardar el PIN.',
            variant: 'destructive',
          });
          return;
        }
      } else {
        const updatedProfiles = profiles.map(p => {
          if ((p.id ?? p.name) === selectedUser) {
            return {
              ...p,
              pinHash: hash,
              pinSalt: salt,
              pinIterations: iterations,
              pinVersion: version,
              pinUpdatedAt,
            };
          }
          return p;
        });

        await updateProfilesConfig(updatedProfiles);
      }

      setEditingProfile(prev => ({ ...prev, pinHash: hash, pinSalt: salt, pinIterations: iterations, pinVersion: version }));
      setCurrentPinInput('');
      setNewPinInput('');
      setConfirmPinInput('');

      toast({
        title: '✅ PIN guardado',
        description: hasExistingPin ? 'El PIN se ha actualizado y los dispositivos revocados.' : 'Se ha creado un PIN para este perfil.',
        duration: 2500,
      });
    } catch (error) {
      console.error('[ProfileSettingsModal] Error saving PIN:', error);
      toast({
        title: 'Error al guardar el PIN',
        description: 'No se pudo generar/guardar el PIN. Revisa la compatibilidad del navegador.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingPin(false);
    }
  };

  const handleRemovePin = async () => {
    if (!editingProfile?.pinHash || !profiles || !selectedUser) return;

    const profileId = editingProfile.id ?? editingProfile.name;

    setIsRemovingPin(true);
    try {
      if (workspaceIdProp) {
        const revoked = await revokeTrustedDevices(workspaceIdProp, db, profileId);
        if (!revoked) {
          throw new Error('No se pudieron revocar los dispositivos confiables.');
        }
        removeLocalDeviceToken(workspaceIdProp, profileId);
      }

      // Trabajador: elimina el PIN en su propio workerProfiles/{profileId}
      // vía updateOwnWorkerProfile. Nunca escribe en settings/profiles.
      if (isWorker) {
        if (typeof updateOwnWorkerProfile !== 'function') {
          throw new Error('No se pudo eliminar el PIN.');
        }

        const success = await updateOwnWorkerProfile(profileId, {
          pinHash: null,
          pinSalt: null,
          pinIterations: null,
          pinVersion: null,
          pinUpdatedAt: null,
        });

        if (!success) {
          throw new Error('No se pudo eliminar el PIN.');
        }
      } else {
        const updatedProfiles = profiles.map((profile) => {
          if ((profile.id ?? profile.name) !== selectedUser) return profile;

          const {
            pinHash,
            pinSalt,
            pinIterations,
            pinVersion,
            pinUpdatedAt,
            ...profileWithoutPin
          } = profile;

          return profileWithoutPin;
        });

        await updateProfilesConfig(updatedProfiles);
      }

      setEditingProfile((previous) => {
        if (!previous) return previous;

        const {
          pinHash,
          pinSalt,
          pinIterations,
          pinVersion,
          pinUpdatedAt,
          ...profileWithoutPin
        } = previous;

        return profileWithoutPin;
      });

      unlockProfile(profileId);
      setCurrentPinInput('');
      setNewPinInput('');
      setConfirmPinInput('');
      setShowRemovePinConfirm(false);

      toast({
        title: '🔓 PIN eliminado',
        description: 'El perfil ya no está protegido por PIN y sus dispositivos confiables han sido revocados.',
        duration: 2500,
      });
    } catch (error) {
      console.error('[ProfileSettingsModal] Error removing PIN:', error);
      toast({
        title: 'Error al quitar el PIN',
        description: 'No se pudo quitar el PIN de forma segura. Inténtalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsRemovingPin(false);
    }
  };


  /**
   * Selecciona un archivo de imagen local: valida tipo, crea preview y
   * lo deja pendiente de subida (no sube a Firebase hasta Guardar).
   */
  const handleImageFileSelect = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Archivo no válido',
        description: 'Selecciona un archivo de imagen.',
        variant: 'destructive',
      });
      return;
    }

    if (pendingImagePreview) {
      URL.revokeObjectURL(pendingImagePreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setPendingImageFile(file);
    setPendingImagePreview(previewUrl);
    setPendingImageDelete(false);
  };

  /**
   * Marca la imagen para eliminar. No borra de Storage todavía:
   * el borrado real ocurre solo al Guardar, y solo si no hay imagen nueva sustituta.
   */
  const handleImageRemove = () => {
    if (pendingImagePreview) {
      URL.revokeObjectURL(pendingImagePreview);
    }
    setPendingImageFile(null);
    setPendingImagePreview(null);
    setPendingImageDelete(true);
  };

  const handleSave = async () => {
    if (!editingProfile || !selectedUser || !profiles) {
      return;
    }

    // Validar teléfono si está configurado
    const phoneValidation = validatePhoneNumber(editingProfile.whatsapp || '');
    if (!phoneValidation.valid && editingProfile.whatsapp) {
      toast({
        title: 'Error de validación',
        description: phoneValidation.message,
        variant: 'destructive',
      });
      return;
    }

    // Trabajador: puede guardar name, whatsapp, webUrl y requireConfirmationWhenMarking
    // en su propio perfil, vía updateOwnWorkerProfile (workerProfiles/{profileId}).
    // Nunca escribe en settings/profiles ni sube/borra imágenes.
    if (isWorker) {
      if (typeof updateOwnWorkerProfile !== 'function') {
        toast({
          title: 'Error al guardar',
          description: 'No se pudo guardar la configuración del perfil.',
          variant: 'destructive',
        });
        return;
      }

      // Validar nombre no vacío (mismo criterio que el administrador)
      if (!editingProfile.name || editingProfile.name.trim() === '') {
        toast({
          title: 'Error de validación',
          description: 'El nombre del perfil no puede estar vacío.',
          variant: 'destructive',
        });
        return;
      }

      setIsSaving(true);
      try {
        const profileId = editingProfile.id ?? editingProfile.name;
        let workerImageUrl = editingProfile.imageUrl || '';

if (pendingImageFile && workspaceIdProp) {
  setIsUploadingImage(true);
  try {
    const storageRef = ref(
      storage,
      `workspaces/${workspaceIdProp}/profileImages/${profileId}`
    );

    const snapshot = await uploadBytes(storageRef, pendingImageFile);
    workerImageUrl = await getDownloadURL(snapshot.ref);
  } finally {
    setIsUploadingImage(false);
  }
} else if (pendingImageDelete) {
  workerImageUrl = '';
}
        const success = await updateOwnWorkerProfile(profileId, {
          name: editingProfile.name.trim(),
          whatsapp: editingProfile.whatsapp || '',
          webUrl: editingProfile.webUrl || '',
          imageUrl: workerImageUrl,
          requireConfirmationWhenMarking: editingProfile.requireConfirmationWhenMarking ?? false,
        });

        if (!success) {
          toast({
            title: 'Error al guardar',
            description: 'No se pudo guardar la configuración del perfil.',
            variant: 'destructive',
          });
          return;
        }

        toast({
          title: '¡Guardado!',
          description: 'La configuración del perfil se ha actualizado correctamente.',
          duration: 2000,
        });

        setEditingProfile(null);
        onOpenChange(false);
      } catch (error) {
        console.error('[ProfileSettingsModal] Error saving worker profile:', error);
        toast({
          title: 'Error al guardar',
          description: 'No se pudo guardar la configuración del perfil.',
          variant: 'destructive',
        });
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Validar nombre no vacío (solo administrador)
    if (!editingProfile.name || editingProfile.name.trim() === '') {
      toast({
        title: 'Error de validación',
        description: 'El nombre del perfil no puede estar vacío.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {

      const profileId = editingProfile.id ?? editingProfile.name;

      // Construir el perfil final incorporando cambios de imagen pendientes.
      // No se usa editingProfile.imageUrl directamente para evitar estado obsoleto.
      let finalProfile = { ...editingProfile };

      if (pendingImageFile && workspaceIdProp) {
        // Caso A: hay imagen nueva pendiente de subir (sustituye a la anterior en la misma ruta)
        setIsUploadingImage(true);
        try {
          const storageRef = ref(storage, `workspaces/${workspaceIdProp}/profileImages/${profileId}`);
          const snapshot = await uploadBytes(storageRef, pendingImageFile);
          const downloadUrl = await getDownloadURL(snapshot.ref);
          finalProfile = { ...finalProfile, imageUrl: downloadUrl };
        } finally {
          setIsUploadingImage(false);
        }
      } else if (pendingImageDelete) {
        // Caso B: se marcó eliminar y no hay archivo nuevo que la sustituya
        finalProfile = { ...finalProfile, imageUrl: '' };
      }

      // Actualizar solo el perfil activo dentro del array
      const updatedProfiles = profiles.map(p => {
        if ((p.id ?? p.name) === selectedUser) {
          // Preservar todos los campos desconocidos mediante spread
          return {
            ...p,
            ...finalProfile,
          };
        }
        return p;
      });

      // Llamar a la función de actualización
      await updateProfilesConfig(updatedProfiles);

      // Borrar la imagen remota solo después de guardar el perfil correctamente,
      // y solo si se marcó eliminar sin subir una imagen nueva que la sustituya
      if (pendingImageDelete && !pendingImageFile && workspaceIdProp) {
        try {
          const storageRef = ref(storage, `workspaces/${workspaceIdProp}/profileImages/${profileId}`);
          await deleteObject(storageRef);
        } catch (deleteError) {
          console.warn('[ProfileSettingsModal] No se pudo borrar la imagen de Storage:', deleteError.message);
        }
      }

      // Limpiar estado pendiente de imagen tras guardar
      if (pendingImagePreview) {
        URL.revokeObjectURL(pendingImagePreview);
      }
      setPendingImageFile(null);
      setPendingImagePreview(null);
      setPendingImageDelete(false);

      toast({
        title: '¡Guardado!',
        description: 'La configuración del perfil se ha actualizado correctamente.',
        duration: 2000,
      });

      setEditingProfile(null);
      onOpenChange(false);
    } catch (error) {
      console.error('[ProfileSettingsModal] Error saving profile:', error);
      toast({
        title: 'Error al guardar',
        description: 'No se pudo guardar la configuración del perfil.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-md max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {editingProfile && `Configuración de ${editingProfile.name}`}
            </DialogTitle>
          </DialogHeader>

          {editingProfile && (
              <div className="space-y-4 py-4">
                {/* Tarjeta 1: Datos del perfil */}
                <div className="space-y-4 p-4 border rounded-lg bg-card">
                  <h3 className="text-lg font-semibold">Datos del perfil</h3>

                  {/* Nombre del Perfil: solo administrador puede editarlo */}
                  <div>
                    <Label htmlFor="profile-name" className="text-sm font-medium">
                      Nombre del Perfil
                    </Label>
                    <Input
                      id="profile-name"
                      value={editingProfile.name || ''}
                      onChange={(e) =>
                        setEditingProfile({ ...editingProfile, name: e.target.value })
                      }
                      placeholder="Nombre del perfil"
                      className="mt-1"
                    />
                  </div>


                  {/* WhatsApp / Teléfono */}
                  <div>
                    <Label htmlFor="profile-whatsapp" className="text-sm font-medium">
                      WhatsApp (teléfono)
                    </Label>
                    <Input
                      id="profile-whatsapp"
                      value={editingProfile.whatsapp || ''}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          whatsapp: e.target.value,
                        })
                      }
                      placeholder="+34 6XX XXX XXX"
                      className="mt-1"
                    />
                    {editingProfile.whatsapp &&
                      !validatePhoneNumber(editingProfile.whatsapp).valid && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-amber-600">
                          <AlertCircle className="h-3 w-3" />
                          <span>
                            {validatePhoneNumber(editingProfile.whatsapp).message}
                          </span>
                        </div>
                      )}
                  </div>
                </div>

                {/* Tarjeta 2: Accesos rápidos (solo lectura para el trabajador: los configura el administrador) */}
                <div className="space-y-4 p-4 border rounded-lg bg-card">
                  <h3 className="text-lg font-semibold">Accesos rápidos</h3>

                  {/* Web de acceso rápido */}
                  <div>
                    <Label htmlFor="profile-weburl" className="text-sm font-medium">
                      Web de acceso rápido (producto, tiempo, web oficial...)
                    </Label>
                    <Input
                      id="profile-weburl"
                      value={editingProfile.webUrl || ''}
                      onChange={(e) =>
                        setEditingProfile({ ...editingProfile, webUrl: e.target.value })
                      }
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>

                  {/* Imagen de acceso rápido */}
                  {(
                    <div>
                      <Label className="text-sm font-medium">
                        Imagen de acceso rápido (planos, plazas, normas...)
                      </Label>
                      <input
                        id="profile-image-file"
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileSelect}
                        className="hidden"
                      />
                      <div className="flex items-center gap-3 mt-2">
                        {(pendingImagePreview || (editingProfile.imageUrl && !pendingImageDelete)) && (
                          <img
                            src={pendingImagePreview || editingProfile.imageUrl}
                            alt="preview"
                            className="h-12 w-12 object-cover rounded border"
                          />
                        )}
                        <div className="flex flex-col gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isUploadingImage}
                            onClick={() => document.getElementById('profile-image-file')?.click()}
                          >
                            {pendingImagePreview || (editingProfile.imageUrl && !pendingImageDelete)
                              ? 'Cambiar imagen'
                              : 'Subir imagen'}
                          </Button>
                          {(pendingImagePreview || (editingProfile.imageUrl && !pendingImageDelete)) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={isUploadingImage}
                              onClick={handleImageRemove}
                              className="text-destructive hover:text-destructive"
                            >
                              Eliminar imagen
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                </div>


                {/* Tarjeta 3: Confirmar acciones */}
                <div className="space-y-4 p-4 border rounded-lg bg-card">
                  <h3 className="text-lg font-semibold">Confirmar acciones</h3>

                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label
                        htmlFor="confirm-marking"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Confirmar acciones importantes
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Pide confirmación antes de marcar clientes, anularlos o cambiar
                        estados de ruta.
                      </p>
                    </div>
                    <Switch
                      id="confirm-marking"
                      checked={editingProfile.requireConfirmationWhenMarking ?? false}
                      onCheckedChange={(checked) =>
                        setEditingProfile({
                          ...editingProfile,
                          requireConfirmationWhenMarking: checked,
                        })
                      }
                    />
                  </div>
                </div>

                {/* Tarjeta 4: Configuración del PIN (compartida entre administrador y trabajador) */}
                <div className="space-y-4 p-4 border rounded-lg bg-card">

                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">
                      {editingProfile.pinHash ? 'Cambiar PIN de este perfil' : 'Configurar PIN para este perfil'}
                    </h3>
                  </div>

                  {!editingProfile.pinHash && (
                    <p className="text-xs text-muted-foreground">
                      Sin PIN configurado. Puedes proteger este perfil creando uno de 4 a 6 dígitos.
                    </p>
                  )}

                  {editingProfile.pinHash && (
                    <div>
                      <Label htmlFor="current-pin" className="text-xs text-muted-foreground">
                        PIN actual
                      </Label>
                      <Input
                        id="current-pin"
                        type="password"
                        value={currentPinInput}
                        onChange={(e) => setCurrentPinInput(e.target.value)}
                        placeholder="PIN actual"
                        maxLength="6"
                        className="mt-1"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="new-pin" className="text-xs text-muted-foreground">
                      PIN nuevo (4 a 6 dígitos)
                    </Label>
                    <Input
                      id="new-pin"
                      type="password"
                      value={newPinInput}
                      onChange={(e) => setNewPinInput(e.target.value)}
                      placeholder="0000"
                      maxLength="6"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm-pin" className="text-xs text-muted-foreground">
                      Repetir PIN nuevo
                    </Label>
                    <Input
                      id="confirm-pin"
                      type="password"
                      value={confirmPinInput}
                      onChange={(e) => setConfirmPinInput(e.target.value)}
                      placeholder="0000"
                      maxLength="6"
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSavePin}
                    disabled={isSavingPin}
                    className="w-full"
                  >
                    {isSavingPin ? 'Guardando PIN...' : editingProfile.pinHash ? 'Cambiar PIN' : 'Guardar PIN'}
                  </Button>

                  {editingProfile.pinHash && (
                    <>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => setShowRemovePinConfirm(true)}
                        disabled={isRemovingPin}
                        className="w-full flex items-center gap-2"
                      >
                        <ShieldOff className="h-4 w-4" />
                        {isRemovingPin ? 'Quitando PIN...' : 'Quitar PIN'}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRevokeDevices}
                        className="w-full border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-800"
                      >
                        Quitar acceso guardado en otros dispositivos
                      </Button>

                      <p className="text-xs text-muted-foreground text-center">
                        Los móviles y navegadores autorizados tendrán que introducir el PIN de nuevo.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}



            <DialogFooter className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-primary hover:bg-primary/90"
              >
                {isSaving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showRemovePinConfirm}
        onOpenChange={(isOpen) => {
          if (!isRemovingPin) setShowRemovePinConfirm(isOpen);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el PIN de este perfil?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la protección por PIN y se revocarán todos los dispositivos confiables de este perfil. Después podrá entrar sin PIN.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemovingPin}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleRemovePin}
              disabled={isRemovingPin}
            >
              {isRemovingPin ? 'Quitando...' : 'Quitar PIN'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}